/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useFeatureFlag } from '@/features/globalConfig';
import { type InfiniteData, type QueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { type AxiosInstance } from 'axios';
import { getThirdPartyAxios } from '@/lib/axios/thirdPartyClient';
import { type GetCityStoresResponse, type GetCityStoresQueryParams } from '../interface/yext-types';
import { useLocationsParams } from '../hooks/useLocationsParams';
import { LOCATIONS_STORES_PAGE_SIZE } from '../constants/locationsAPI';
import { type Locale } from '@/types/i18n';
import { useLocale } from '@/hooks/useLocale';
import { locationsPrefix } from '../constants/locationsPrefix';
import { getYextCountryCodes } from '../utils/getYextCountryCodes';

export type GetCityStoresOptions = {
  yextURL: string;
  yextAPIKey: string;
  yextVersion: string;
  locale: Locale;
  state: string;
  city: string;
  limit?: number;
  offset?: number;
};

const getCityStores = async (options: GetCityStoresOptions, _axiosInstance?: AxiosInstance) => {
  const { yextURL, yextAPIKey, yextVersion, locale, state, city, limit = 50, offset = 0 } = options;

  const missingYextParams: string[] = [];

  if (!yextURL) {
    missingYextParams.push('YEXT_URL');
  }
  if (!yextAPIKey) {
    missingYextParams.push('YEXT_API_KEY');
  }
  if (!yextVersion) {
    missingYextParams.push('YEXT_VERSION');
  }

  if (missingYextParams.length > 0) {
    throw new Error(`Missing Yext parameters in globalConfig: ${missingYextParams.join(', ')}`);
  }

  if (!state || !city) {
    throw new Error('State and city are required to fetch city stores');
  }

  const locationsHome = locationsPrefix[locale];

  const params: GetCityStoresQueryParams = {
    entityType: 'location',
    limit,
    offset,
    fields: [
      'name',
      'address',
      'hours',
      'timezone',
      'additionalHoursText',
      'closed',
      'slug',
      'websiteUrl',
    ].join(','),
    filter: JSON.stringify({
      countryCode: { $in: getYextCountryCodes(locale) },
      'websiteUrl.url': {
        $contains: `/${locationsHome}/${encodeURIComponent(state)}/${encodeURIComponent(city)}/`,
      },
      closed: { $eq: false },
    }),
    v: yextVersion,
  };

  const client = getThirdPartyAxios();

  const response = await client.get<GetCityStoresResponse>(yextURL + 'me/entities', {
    params,
    headers: {
      'api-key': yextAPIKey,
    },
  });

  return response.data;
};

export const useCityStoresInfinite = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const yextURL = useFeatureFlag('YEXT_URL') ?? '';
  const yextAPIKey = useFeatureFlag('YEXT_API_KEY') ?? '';
  const yextVersion = useFeatureFlag('YEXT_VERSION') ?? '';

  const locale = useLocale();
  const { city = '', state = '' } = useLocationsParams();

  const limit = LOCATIONS_STORES_PAGE_SIZE;

  return useInfiniteQuery({
    queryKey: [
      'cityStores',
      'infinite',
      { state, city, limit, locale, yextURL, yextAPIKey, yextVersion },
    ] as const,
    queryFn: async ({ pageParam = 0, queryKey }) => {
      return getCityStores({
        ...queryKey[2],
        offset: pageParam,
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalLoaded = allPages.reduce((sum, page) => sum + page.response.entities.length, 0);
      const totalCount = lastPage.response.count;

      if (totalLoaded < totalCount) {
        return totalLoaded;
      }

      return undefined;
    },
    initialPageParam: 0,
    enabled: enabled && Boolean(yextURL && yextAPIKey && yextVersion && state && city),
  });
};

export const prefetchCityStoresInfinite = async (
  queryClient: QueryClient,
  axios: AxiosInstance,
  options: Omit<GetCityStoresOptions, 'offset'>,
) => {
  await queryClient.prefetchInfiniteQuery({
    queryKey: ['cityStores', 'infinite', options] as const,
    queryFn: async ({ pageParam = 0, queryKey }) => {
      return getCityStores(
        {
          ...queryKey[2],
          offset: pageParam,
        },
        axios,
      );
    },
    getNextPageParam: (lastPage: GetCityStoresResponse, allPages: GetCityStoresResponse[]) => {
      const totalLoaded = allPages.reduce(
        (sum: number, page: GetCityStoresResponse) => sum + page.response.entities.length,
        0,
      );
      const totalCount = lastPage.response.count;

      if (totalLoaded < totalCount) {
        return totalLoaded;
      }

      return undefined;
    },
    initialPageParam: 0,
  });
};

export const getCityStoresDataFromCache = (
  queryClient: QueryClient,
  options: Omit<GetCityStoresOptions, 'offset'>,
) => {
  return queryClient.getQueryData<InfiniteData<GetCityStoresResponse>>([
    'cityStores',
    'infinite',
    options,
  ]);
};
